import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { RoleManagementHandler, RoleDetail, OperationItem } from './RoleManagementHandler';

interface RoleFormModalProps {
    role: RoleDetail | null; // null = create mode
    onClose: () => void;
    onSaved: () => void;
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({ role, onClose, onSaved }) => {
    const { theme } = useTheme();

    const isEdit = role !== null;
    const [name, setName] = useState(role?.name || '');
    const [description, setDescription] = useState(role?.description || '');
    const [selectedOperationIds, setSelectedOperationIds] = useState<string[]>([]);
    const [operations, setOperations] = useState<OperationItem[]>([]);
    const [opsLoading, setOpsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setOpsLoading(true);
            try {
                const handler = new RoleManagementHandler();
                if (isEdit && role?.id) {
                    const details = await handler.getRoleDetails(role.id);
                    setSelectedOperationIds(details?.claims || []);
                    setOperations((details?.allOperations || []).map((opName: string) => ({ id: opName, name: opName })));
                } else {
                    const response = await handler.getOperations();
                    setOperations(response?.operations || response || []);
                }
            } catch (err) {
                console.error('Error fetching operations:', err);
                setOperations([]);
            } finally {
                setOpsLoading(false);
            }
        };
        fetchData();
    }, []);

    const toggleOperation = (operationId: string) => {
        setSelectedOperationIds((prev) =>
            prev.includes(operationId)
                ? prev.filter((id) => id !== operationId)
                : [...prev, operationId]
        );
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Role name is required.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const handler = new RoleManagementHandler();
            if (isEdit && role?.id) {
                await handler.updateRole(role.id, name.trim(), description.trim(), selectedOperationIds);
            } else {
                await handler.createRole(name.trim(), description.trim(), selectedOperationIds);
            }
            onSaved();
        } catch (err) {
            console.error('Error saving role:', err);
            setError('Failed to save role. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const styles: Record<string, React.CSSProperties> = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            background: theme.card,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '550px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto' as const,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
        },
        title: {
            fontSize: '20px',
            fontWeight: 700,
            color: theme.text,
        },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '20px',
            color: theme.textSecondary,
            cursor: 'pointer',
            padding: '4px 8px',
        },
        fieldGroup: {
            marginBottom: '16px',
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: theme.text,
            marginBottom: '6px',
        },
        input: {
            width: '100%',
            padding: '10px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box' as const,
        },
        textarea: {
            width: '100%',
            padding: '10px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box' as const,
            minHeight: '80px',
            resize: 'vertical' as const,
            fontFamily: 'inherit',
        },
        operationsSection: {
            marginBottom: '20px',
        },
        operationsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '8px',
            maxHeight: '250px',
            overflowY: 'auto' as const,
            padding: '8px',
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            background: theme.inputBg,
        },
        operationItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background 0.2s',
        },
        operationCheckbox: {
            width: '16px',
            height: '16px',
            cursor: 'pointer',
            accentColor: '#3b82f6',
            flexShrink: 0,
        },
        operationLabel: {
            fontSize: '13px',
            color: theme.text,
            cursor: 'pointer',
        },
        operationDescription: {
            fontSize: '11px',
            color: theme.textSecondary,
        },
        footer: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.border}`,
        },
        cancelButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 500,
            color: theme.text,
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            cursor: 'pointer',
        },
        saveButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#3b82f6',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            opacity: saving ? 0.6 : 1,
        },
        errorText: {
            color: '#ef4444',
            fontSize: '14px',
            marginBottom: '12px',
        },
        spinner: {
            display: 'inline-block',
            width: '14px',
            height: '14px',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <span style={styles.title}>{isEdit ? 'Edit Role' : 'Create Role'}</span>
                    <button style={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                {error && <div style={styles.errorText}>{error}</div>}

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Role Name</label>
                    <input
                        type="text"
                        placeholder="Enter role name..."
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        style={styles.input}
                    />
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Description</label>
                    <textarea
                        placeholder="Enter role description..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        style={styles.textarea}
                    />
                </div>

                <div style={styles.operationsSection}>
                    <label style={styles.label}>
                        Operations ({selectedOperationIds.length} selected)
                    </label>
                    {opsLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px' }}>
                            <div style={styles.spinner} />
                            <span style={{ color: theme.textSecondary, fontSize: '13px' }}>Loading operations...</span>
                        </div>
                    ) : operations.length === 0 ? (
                        <div style={{ padding: '16px', color: theme.textSecondary, fontSize: '13px', fontStyle: 'italic' }}>
                            No operations available
                        </div>
                    ) : (
                        <div style={styles.operationsGrid}>
                            {operations.map((op) => {
                                const isChecked = selectedOperationIds.includes(op.id || '');
                                return (
                                    <div
                                        key={op.id}
                                        style={{
                                            ...styles.operationItem,
                                            background: isChecked ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                        }}
                                        onClick={() => op.id && toggleOperation(op.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => op.id && toggleOperation(op.id)}
                                            style={styles.operationCheckbox}
                                            aria-label={op.name || 'Unnamed'}
                                        />
                                        <div>
                                            <div style={styles.operationLabel}>{op.name || 'Unnamed'}</div>
                                            {op.description && (
                                                <div style={styles.operationDescription}>{op.description}</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div style={styles.footer}>
                    <button style={styles.cancelButton} onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button style={styles.saveButton} onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : isEdit ? 'Update Role' : 'Create Role'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RoleFormModal;
